import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
// @mui
import {
  Card,
  Stack,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Switch,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Select,
  Box,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import AddLocationOutlinedIcon from '@mui/icons-material/AddLocationOutlined';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Autocomplete from '@mui/material/Autocomplete';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// sections / services
import api from 'src/services/BaseService';
import AddUserForStoreModal from 'src/components/modals/AddUserForStoreModal';
import AddLocationForStoreModal from 'src/components/modals/AddLocationForStoreModal';

// ----------------------------------------------------------------------

export default function EditStore() {
  const { id, mallID } = useParams();

  const [showResultMessage, setShowResultMessage] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  const [activeStep, setActiveStep] = useState(0);
  const [userList, setUserList] = useState([]);

  const [locationName, setLocationName] = useState('');
  const [editStore, setEditStore] = useState(null);

  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addLocationModalOpen, setAddLocationModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(0);
  const [selectedStoreLocations, setSelectedStoreLocations] = useState([]);

  // ---- Kurumsal cache (bu sayfada bir kez çekilecek) ----
  const [corpLoading, setCorpLoading] = useState(false);
  const [corpLoaded, setCorpLoaded] = useState(false);
  const [corpTitles, setCorpTitles] = useState([]); // Autocomplete options
  const [corpMap, setCorpMap] = useState(new Map()); // title -> corpObj

  const fetchCorporationsOnce = async () => {
    if (corpLoaded || corpLoading) return;
    try {
      setCorpLoading(true);
      const res = await api.post('flow/get-corporations');
      const raw = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.result || res?.data?.rows || [];
      const map = new Map();
      raw.forEach((corp) => {
        const title = String(corp?.title || '').trim();
        if (!title) return;
        map.set(title, {
          title,
          companyCorporationalCode: String(
            corp?.companyCorporationalCode || ''
          ),
          billing: {
            taxNo: String(corp?.billing?.taxNo || ''),
            taxAdministration: String(corp?.billing?.taxAdministration || ''),
            address: String(corp?.billing?.address || ''),
            region: String(corp?.billing?.region || ''),
            city: String(corp?.billing?.city || ''),
            country: String(corp?.billing?.country || 'Türkiye'),
            email: String(corp?.billing?.email || ''),
            neighborhood: String(corp?.billing?.neighborhood || ''),
          },
        });
      });
      setCorpMap(map);
      setCorpTitles(Array.from(map.keys()));
      setCorpLoaded(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('flow/get-corporations error:', e);
      toast.error('Kurumsal listesi alınamadı.');
      setCorpMap(new Map());
      setCorpTitles([]);
      setCorpLoaded(false);
    } finally {
      setCorpLoading(false);
    }
  };

  const corpReady = !corpLoading && corpTitles.length > 0;

  const [formData, setFormData] = useState({
    mallID: '',
    title: '',
    companyLegalTitle: '',
    companyCorporationalCode: '',
    taxNo: '',
    taxAdministration: '',
    address: '',
    city: '',
    region: '',
    country: 'Türkiye',
    isPrimary: null,
    isGetir: null,
    mallTitle: '',
    locationAddress: '',
    locations: [],
    branchID: '',
    getirID: '',
    allowedAPIs: null,
  });

  const typeList = [
    { name: 'Mağaza', value: 'store' },
    { name: 'Depo', value: 'warehouse' },
  ];

  const steps = ['Mağaza Bilgileri', 'Lokasyon Bilgileri'];

  // Autocomplete değerinin listede yer almasını garanti et (edit başlangıcı için)
  const autoOptions = useMemo(() => {
    if (!corpReady) return [];
    const current = (formData.companyLegalTitle || '').trim();
    return current && !corpTitles.includes(current)
      ? [...corpTitles, current]
      : corpTitles;
  }, [corpReady, corpTitles, formData.companyLegalTitle]);

  const selectNewUser = (newUser) => {
    getUserList();
    const updatedFormData = { ...formData };
    const locationToUpdate = updatedFormData.locations[expanded];
    locationToUpdate.userID = newUser;
    updatedFormData.locations[expanded] = locationToUpdate;
    setFormData(updatedFormData);
  };

  useEffect(() => {
    // Kurumsal liste ve mağaza detaylarını birlikte hazırlayalım
    fetchCorporationsOnce();
    getMallsAndStores();
    getUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserList = () => {
    api
      .post('users/get-users', { type: 'corporational', mallID, status: '1' })
      .then((response) => {
        if (response.data.status) setUserList(response.data.result);
      })
      .catch(() => {});
  };

  function getMallsAndStores() {
    api
      .post('/malls/get-malls', { withOffices: true })
      .then((response) => {
        if (response.data) getStores(response.data.result);
      })
      .catch(() => {});
  }

  function getStores(malls) {
    api
      .post('flow/get-malls-branches', { mallID })
      .then((response) => {
        if (!response.data) return;

        const mallTitle = malls.find((item) => item.id === mallID)?.title || '';
        const found = response.data.result.find((item) => item.id === id);

        const filteredLocations = (found?.locations || []).filter(
          (loc) => String(loc?.locationStatus) !== '0'
        );

        let checkIsGetir = false;
        if (found?.allowedAPIs?.includes('getir')) checkIsGetir = true;

        setSelectedStoreLocations(filteredLocations);

        const isPrimaryBoolean = found?.isPrimary === '1';

        const base = {
          mallID: found.mallID,
          title: found.title,
          companyLegalTitle: found.companyLegalTitle,
          companyCorporationalCode: found.companyCorporationalCode,
          taxNo: found.billing?.taxNo,
          taxAdministration: found.billing?.taxAdministration,
          address: found.billing?.address,
          city: found.billing?.city,
          region: found.billing?.region,
          country: found.billing?.country || 'Türkiye',
          isPrimary: isPrimaryBoolean,
          mallTitle,
          locationAddress: found.locationAddress,
          locations: filteredLocations,
          branchID: found.branchID,
          getirID: found.branchAPIIDs?.getir ? found.branchAPIIDs.getir : '',
          isGetir: checkIsGetir,
          allowedAPIs: found.allowedAPIs,
        };

        setEditStore(base);
        setFormData(base);
      })
      .catch(() => {});
  }

  const handleCloseAddUserModal = () => setAddUserModalOpen(false);
  const handleOpenAddUserModal = () => setAddUserModalOpen(true);

  const handleCloseAddLocationModal = () => setAddLocationModalOpen(false);
  const handleOpenAddLocationModal = () => setAddLocationModalOpen(true);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  const allFieldsAreValid = () => {
    if (activeStep === 0) {
      if (
        formData.title !== '' &&
        formData.companyLegalTitle !== '' &&
        formData.taxNo !== ''
      ) {
        if (!formData.isGetir) return true;
        return formData.getirID !== '';
      }
      return false;
    } else if (activeStep === 1) {
      if (formData.mallID !== '' && locationName !== '') {
        return true;
      }
      return false;
    }
  };

  const handleSwitchChange = (event) => {
    const { name } = event.target;
    if (name === 'isPrimary') {
      setFormData((prev) => ({ ...prev, isPrimary: !prev.isPrimary }));
    } else if (name === 'isGetir') {
      setFormData((prev) => ({ ...prev, isGetir: !prev.isGetir }));
    }
  };

  const handleLocationNameChange = (value, index) => {
    const updatedFormData = { ...formData };
    const locationToUpdate = updatedFormData.locations[index];
    locationToUpdate.title = value;
    updatedFormData.locations[index] = locationToUpdate;
    setFormData(updatedFormData);
  };

  const handleLocationUserChange = (value, index) => {
    const updatedFormData = { ...formData };
    const locationToUpdate = updatedFormData.locations[index];
    locationToUpdate.userID = value;
    updatedFormData.locations[index] = locationToUpdate;
    setFormData(updatedFormData);
  };

  const handleLocationTypeChange = (value, index) => {
    const updatedFormData = { ...formData };
    const locationToUpdate = updatedFormData.locations[index];
    locationToUpdate.type = value;
    updatedFormData.locations[index] = locationToUpdate;
    setFormData(updatedFormData);
  };

  // Sadece serbest alanları güncelle (cari alanlar dropdown’dan gelir ve disabled)
  const handleTextFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const nextStep = () => activeStep !== 2 && setActiveStep((s) => s + 1);
  const backStep = () => activeStep !== 0 && setActiveStep((s) => s - 1);

  const removeLocation = (location) => {
    api
      .post('locations/delete', { locationID: location.locationID })
      .then((response) => {
        if (response.data.status) {
          toast.success('Lokasyon başarıyla kaldırıldı');
          getMallsAndStores();
          setExpanded(0);
        } else {
          toast.error('Bir hata oluştu');
        }
      })
      .catch(() => {
        toast.error('Bir hata oluştu');
      });
  };

  const updateLocation = (location) => {
    api
      .post('/flow/location-update', {
        locationID: location.locationID,
        title: location.title,
        branchID: formData.branchID,
        address: formData.locationAddress,
        type: location.type,
        adminUserID: location.userID,
        mallID: formData.mallID,
      })
      .then((response) => {
        if (response.data.status) {
          setResultMessage('Lokasyon başarıyla düzenlendi!');
          setShowResultMessage(true);
        } else {
          setResultMessage('Lokasyon düzenlenirken bir sorunla karşılaşıldı!');
          setShowResultMessage(true);
        }
      })
      .catch(() => {});
  };

  // --- Kurumsal seçim → formu otomatik doldur (alanlar DISABLED kalır) ---
  const onSelectCorporate = (newTitle) => {
    const title = (newTitle || '').trim();
    setFormData((prev) => ({ ...prev, companyLegalTitle: title }));
    if (!title) return;
    const corp = corpMap.get(title);
    if (!corp) return;
    const b = corp.billing || {};
    setFormData((prev) => ({
      ...prev,
      companyLegalTitle: corp.title || title,
      companyCorporationalCode: corp.companyCorporationalCode || '',
      taxNo: b.taxNo || '',
      taxAdministration: b.taxAdministration || '',
      address: b.address || '',
      region: b.region || '',
      city: b.city || '',
      country: b.country || 'Türkiye',
    }));
  };

  const handleUpdateStore = () => {
    const isPrimaryBoolean = formData.isPrimary ? '1' : '0';

    // allowedAPIs güncelleme (Getir)
    let allowedAPIsArr = [];
    if (editStore.allowedAPIs !== null && editStore.allowedAPIs !== '') {
      allowedAPIsArr = editStore.allowedAPIs;
      if (!editStore.allowedAPIs.includes('getir') && formData.isGetir) {
        allowedAPIsArr.push('getir');
      } else if (
        editStore.allowedAPIs.includes('getir') &&
        formData.isGetir === false
      ) {
        allowedAPIsArr =
          allowedAPIsArr.length > 1
            ? allowedAPIsArr.filter((item) => item !== 'getir')
            : null;
      }
    } else {
      allowedAPIsArr = formData.isGetir ? ['getir'] : null;
    }

    api
      .post('/flow/branch-update', {
        id: id,
        branchID: formData.branchID,
        title: formData.title,
        mallID: formData.mallID,
        corporationID: '',
        type: 'corporational',
        companyLegalTitle: formData.companyLegalTitle,
        billing: {
          status: true,
          type: 'corporational',
          taxNo: formData.taxNo,
          taxAdministration: formData.taxAdministration,
          address: formData.address,
          region: formData.region,
          city: formData.city,
          country: formData.country,
        },
        companyCorporationalCode: formData.companyCorporationalCode,
        isPrimary: isPrimaryBoolean,
        allowedAPIs: allowedAPIsArr,
      })
      .then((response) => {
        if (response.data.status) {
          if (!formData.isGetir) {
            setResultMessage('Mağaza başarıyla düzenlendi!');
            setShowResultMessage(true);
            nextStep();
            setEditStore(formData);
          } else {
            api
              .post('/flow/set-branch-api-id', {
                branchID: formData.branchID,
                customerID: String(formData.getirID || '').trim(),
                apiUserID: '378924692387468742',
              })
              .then((resp2) => {
                if (resp2.data.status) {
                  setResultMessage('Mağazaya getir tanımlandı!');
                  setShowResultMessage(true);
                  nextStep();
                  setEditStore(formData);
                } else {
                  setResultMessage(
                    'Mağazaya getir tanımlanırken bir sorun yaşandı!'
                  );
                  setShowResultMessage(true);
                }
              })
              .catch(() => {
                setResultMessage(
                  'Mağazaya getir tanımlanırken bir sorun yaşandı!'
                );
                setShowResultMessage(true);
              });
          }
        } else {
          setResultMessage('Mağaza düzenlenirken bir sorunla karşılaşıldı!');
          setShowResultMessage(true);
        }
      })
      .catch(() => {});
  };

  return (
    <>
      <Helmet>
        <title> Mağaza Düzenleme | Mall Logistics Panel </title>
      </Helmet>

      <Stack
        style={{
          paddingInline: '25px',
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Card
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '45%',
          }}
        >
          <Stack
            width="95%"
            direction="row"
            alignItems="flex-end"
            justifyContent="flex-end"
            marginTop={3}
            mb={5}
          >
            {activeStep === 0 && (
              <div style={{ flexDirection: 'column' }}>
                <div
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: 10,
                  }}
                >
                  <Typography>Öncelikli Mağaza</Typography>
                  <Switch
                    checked={!!formData.isPrimary}
                    onChange={handleSwitchChange}
                    name="isPrimary"
                    color="primary"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                </div>

                <div
                  style={{
                    flexDirection: 'row',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: 10,
                  }}
                >
                  <Typography>Getir</Typography>
                  <Switch
                    checked={!!formData.isGetir}
                    onChange={handleSwitchChange}
                    name="isGetir"
                    color="primary"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                </div>
              </div>
            )}
          </Stack>

          <Stack
            direction="column"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Typography variant="h4" gutterBottom>
              Mağaza Düzenleme
            </Typography>
            <Typography gutterBottom>
              Lütfen düzenlemek istediğiniz mağaza bilgilerini güncelleyin.
            </Typography>
          </Stack>

          <Box sx={{ width: '75%' }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* STEP 1 */}
          {activeStep === 0 && (
            <DialogContent>
              <>
                <Grid xs={16} container alignItems="center" spacing={2}>
                  {/* Mağaza Adı (serbest) */}
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Mağaza Adı*"
                      name="title"
                      value={formData.title || ''}
                      onChange={handleTextFieldChange}
                      margin="normal"
                    />
                  </Grid>

                  {/* Ticari Ünvan (Kurumsal listeden seçilir) */}
                  <Grid item xs={4}>
                    <Autocomplete
                      options={autoOptions}
                      loading={!corpReady}
                      loadingText={'Yükleniyor...'}
                      value={formData.companyLegalTitle || ''}
                      onChange={(_, newValue) =>
                        onSelectCorporate(newValue || '')
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ticari Ünvan*"
                          margin="normal"
                          disabled={!corpReady}
                        />
                      )}
                    />
                  </Grid>

                  {/* Cari Kod (disabled) */}
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Cari Kod"
                      name="companyCorporationalCode"
                      value={formData.companyCorporationalCode || ''}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                </Grid>

                {/* Vergi ve adres alanları — kurumsaldan gelir ve disabled */}
                <Grid xs={16} container alignItems="center" spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Vergi Dairesi"
                      name="taxAdministration"
                      value={formData.taxAdministration || ''}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Vergi Numarası*"
                      type="text"
                      name="taxNo"
                      value={formData.taxNo || ''}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                </Grid>

                <Grid xs={16} container alignItems="center" spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Ülke"
                      name="country"
                      value={formData.country || 'Türkiye'}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Şehir"
                      name="city"
                      value={formData.city || ''}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="İlçe / Bölge"
                      name="region"
                      value={formData.region || ''}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                </Grid>

                <Grid xs={16} container alignItems="center" spacing={2}>
                  <Grid item xs={16}>
                    <TextField
                      fullWidth
                      label="Fatura Adresi"
                      name="address"
                      value={formData.address || ''}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                </Grid>

                {/* Getir ID (Getir aktifse) */}
                {formData.isGetir && (
                  <Grid item xs={12} container alignItems="center" spacing={2}>
                    <Grid
                      xs={12}
                      item
                      sx={{ justifyContent: 'flex-end', display: 'flex' }}
                    >
                      <Grid item xs={12}>
                        <TextField
                          disabled={(editStore?.getirID || '').length > 0}
                          fullWidth
                          label="Getir ID*"
                          type="text"
                          name="getirID"
                          value={formData.getirID || ''}
                          onChange={handleTextFieldChange}
                          margin="normal"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                <Grid xs={16} container alignItems="center" spacing={2}>
                  <Grid item xs={16}>
                    <div
                      style={{
                        height: '1px',
                        backgroundColor: '#EAECF0',
                        margin: '0 15px',
                        marginTop: '32px',
                      }}
                    />
                  </Grid>
                </Grid>

                <Stack
                  direction="column"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={5}
                >
                  {editStore &&
                  (editStore?.title !== formData.title ||
                    editStore?.companyLegalTitle !==
                      formData.companyLegalTitle ||
                    editStore?.companyCorporationalCode !==
                      formData.companyCorporationalCode ||
                    editStore?.taxAdministration !==
                      formData.taxAdministration ||
                    editStore?.taxNo !== formData.taxNo ||
                    editStore?.isPrimary !== formData.isPrimary ||
                    editStore?.isGetir !== formData.isGetir ||
                    editStore?.getirID !== formData.getirID) ? (
                    <Button
                      color="primary"
                      onClick={handleUpdateStore}
                      variant="contained"
                      sx={{
                        marginTop: '32px',
                        width: '241px',
                        height: '50px',
                        backgroundColor: '#1570EF',
                        fontSize: '11px',
                      }}
                      disabled={!allFieldsAreValid() || !corpReady}
                    >
                      KAYDET VE LOKASYON DÜZENLEMEYE İLERLE
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      onClick={nextStep}
                      variant="contained"
                      sx={{
                        marginTop: '32px',
                        width: '241px',
                        height: '50px',
                        backgroundColor: '#1570EF',
                        fontSize: '11px',
                      }}
                      disabled={!allFieldsAreValid() || !corpReady}
                    >
                      LOKASYON DÜZENLEMEYE İLERLE
                    </Button>
                  )}
                </Stack>
              </>
            </DialogContent>
          )}

          {/* STEP 2 */}
          {activeStep === 1 && (
            <DialogContent sx={{ width: '90%' }}>
              <Grid xs={12} container alignItems="flex-end">
                <Grid item xs={4}>
                  <Typography variant="h6">Mağaza Düzenleme</Typography>
                </Grid>
                <Grid
                  item
                  xs={8}
                  sx={{ justifyContent: 'flex-end', display: 'flex' }}
                >
                  <Button
                    color="primary"
                    onClick={handleOpenAddUserModal}
                    variant="contained"
                    sx={{
                      width: '180px',
                      height: '44px',
                      fontSize: 12,
                      fontWeight: 500,
                      backgroundColor: '#079455',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#056235' },
                      marginRight: '8px',
                    }}
                    startIcon={<PersonAddAltOutlinedIcon />}
                  >
                    KULLANICI EKLE
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleOpenAddLocationModal}
                    variant="contained"
                    sx={{
                      width: '180px',
                      height: '44px',
                      fontSize: 12,
                      fontWeight: 500,
                      backgroundColor: '#079455',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#056235' },
                    }}
                    startIcon={<AddLocationOutlinedIcon />}
                  >
                    LOKASYON EKLE
                  </Button>
                </Grid>
              </Grid>

              <div
                style={{
                  height: '1px',
                  backgroundColor: '#EAECF0',
                  marginBottom: '32px',
                  marginTop: '15px',
                  width: '100%',
                }}
              />

              <>
                {formData.locations?.map((location, index) => (
                  <Accordion
                    key={location.locationID || index}
                    expanded={expanded === index}
                    onChange={handleAccordionChange(index)}
                    sx={{
                      borderColor: '#ccc',
                      marginBottom: '15px',
                      borderRadius: 1,
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                      width: '100%',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id={`panel-${index}-header`}
                      sx={{
                        backgroundColor:
                          expanded === index ? '#F3F4F6' : 'transparent',
                        borderBottomColor: '#ccc',
                        borderBottomStyle: 'solid',
                      }}
                      style={{ height: '56px', minHeight: '56px' }}
                    >
                      <Typography>
                        <b>{selectedStoreLocations[index]?.title}</b> —{' '}
                        {formData.mallTitle}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        borderTopColor: '#ccc',
                        borderTopWidth: '1px',
                        borderTopStyle: 'solid',
                      }}
                    >
                      <Typography
                        sx={{ mt: '18px', color: '#9CA3AF', fontSize: '16px' }}
                      >
                        Lokasyon Düzenle
                      </Typography>

                      <Grid xs={16} container alignItems="center" spacing={2}>
                        <Grid item xs={16}>
                          <div
                            style={{
                              height: '2px',
                              backgroundColor: '#EAECF0',
                              width: '100%',
                              marginTop: '15px',
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Grid xs={16} container alignItems="center" spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Lokasyon Adı*"
                            name="locationName"
                            value={location.title}
                            onChange={(e) =>
                              handleLocationNameChange(e.target.value, index)
                            }
                            margin="normal"
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <FormControl fullWidth margin="normal">
                            <InputLabel>Lokasyon Türü</InputLabel>
                            <Select
                              sx={{ width: '100%' }}
                              label="Lokasyon Türü"
                              value={location.type}
                              onChange={(e) =>
                                handleLocationTypeChange(e.target.value, index)
                              }
                              name="locationType"
                            >
                              {typeList.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <TextField
                        fullWidth
                        label="Adres Bilgisi"
                        name="locationAddress"
                        value={formData.locationAddress}
                        onChange={handleTextFieldChange}
                        margin="normal"
                      />

                      <FormControl fullWidth margin="normal">
                        {userList.length === 0 ? (
                          <InputLabel>Kullanıcılar Listeleniyor...</InputLabel>
                        ) : (
                          <InputLabel>Sorumlu Kişi ya da Kullanıcı</InputLabel>
                        )}
                        <Select
                          disabled={userList.length === 0}
                          sx={{ width: '100%' }}
                          label="Sorumlu Kişi ya da Kullanıcı"
                          value={location.userID}
                          onChange={(e) =>
                            handleLocationUserChange(e.target.value, index)
                          }
                          name="selectedUser"
                        >
                          {userList?.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {`${user.name} ${user.lastname}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Grid xs={16} container alignItems="center" spacing={2}>
                        <Grid item xs={16}>
                          <div
                            style={{
                              height: '1px',
                              backgroundColor: '#EAECF0',
                              margin: '0 15px',
                              marginTop: '32px',
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        mb={5}
                      >
                        <Button
                          disabled={formData.locations.length === 1}
                          color="primary"
                          onClick={() => removeLocation(location)}
                          variant="outlined"
                          sx={{
                            color: '#000',
                            borderColor: '#ccc',
                            '&:hover': { borderColor: '#999' },
                            marginTop: '32px',
                            width: '241px',
                            height: '46px',
                            marginRight: '25px',
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          LOKASYONU KALDIR
                        </Button>

                        <Button
                          color="primary"
                          onClick={() => updateLocation(location)}
                          variant="contained"
                          sx={{
                            marginTop: '32px',
                            width: '241px',
                            height: '46px',
                            backgroundColor: '#1570EF',
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          DEĞİŞİKLİKLERİ KAYDET
                        </Button>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                mb={5}
              >
                <Button
                  color="primary"
                  onClick={backStep}
                  variant="outlined"
                  sx={{
                    marginTop: '32px',
                    width: '241px',
                    height: '46px',
                    marginRight: '25px',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  ÖNCEKİ ADIM
                </Button>

                <Button
                  color="primary"
                  href="/dashboard/store-list"
                  variant="contained"
                  sx={{
                    marginTop: '32px',
                    width: '241px',
                    height: '46px',
                    backgroundColor: '#1570EF',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  DÜZENLEMEYİ TAMAMLA
                </Button>
              </Stack>
            </DialogContent>
          )}
        </Card>
      </Stack>

      {addUserModalOpen && (
        <AddUserForStoreModal
          open={addUserModalOpen}
          onClose={handleCloseAddUserModal}
          branchID={formData.branchID}
          mallID={formData.mallID}
          refreshPage={selectNewUser}
        />
      )}

      {addLocationModalOpen && (
        <AddLocationForStoreModal
          open={addLocationModalOpen}
          onClose={handleCloseAddLocationModal}
          branchID={formData.branchID}
          mallID={mallID}
          refreshPage={getMallsAndStores}
        />
      )}

      <Dialog
        open={showResultMessage}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Mağaza Düzenleme'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {resultMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResultMessage(false)}>Tamam</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
